import React, { useState, Suspense, lazy, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import Header from '../../components/Header';
import { useTheme } from '../../hooks/useTheme';
import { useBackendStatus } from '../../hooks/useBackendApi';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import NavigationRail, { NAV_RAIL_COLLAPSED_WIDTH } from '../../components/navigation/NavigationRail';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

const RegistriesAdminTab = lazy(() => import('./RegistriesAdminTab'));
const ClusterConfigAdminTab = lazy(() => import('./ClusterConfigAdminTab'));
const GitlabAdminTab = lazy(() => import('./GitlabAdminTab'));
const RelationsAdminTab = lazy(() => import('./RelationsAdminTab'));
const UsersAdminTab = lazy(() => import('./UsersAdminTab'));

const BASE_TABS = [
  { label: 'Реестры', component: RegistriesAdminTab, icon: <StorageOutlinedIcon fontSize="small" /> },
  { label: 'K8s кластеры', component: ClusterConfigAdminTab, icon: <HubOutlinedIcon fontSize="small" /> },
  { label: 'GitLab & Синхронизация', component: GitlabAdminTab, icon: <SyncOutlinedIcon fontSize="small" /> },
  { label: 'Связи', component: RelationsAdminTab, icon: <AccountTreeOutlinedIcon fontSize="small" /> },
];

const AdminPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [railWidth, setRailWidth] = useState(NAV_RAIL_COLLAPSED_WIDTH);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { usingBackend } = useBackendStatus();
  const { hasRole } = useAuth();
  const { currentProject, projects, setCurrentProject } = useProject();

  const TABS = usingBackend && hasRole('admin')
    ? [...BASE_TABS, { label: 'Пользователи', component: UsersAdminTab, icon: <PeopleAltOutlinedIcon fontSize="small" /> }]
    : BASE_TABS;

  const TabContent = TABS[Math.min(tab, TABS.length - 1)].component;
  const railItems = TABS.map((item, index) => ({
    id: `admin-${index}`,
    label: item.label,
    icon: item.icon,
    active: tab === index,
    onClick: () => setTab(index),
  }));

  // Translate our custom theme into MUI palette so all MUI components in tabs
  // (Table, Button, Typography, Alert, TextField, Chip, Divider…) pick up the right colors.
  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: theme.name === 'dark' ? 'dark' : 'light',
      background: {
        default: theme.background,
        paper: theme.codePanel.background,
      },
      text: {
        primary: theme.toolbar.text,
        secondary: theme.name === 'dark' ? '#9ca3af' : '#6b7280',
      },
      divider: theme.toolbar.buttonBorder,
      primary: {
        main: theme.name === 'dark' ? '#3b82f6' : '#2563eb',
      },
      error: { main: '#ef4444' },
      success: { main: '#22c55e' },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: theme.toolbar.buttonBorder,
            color: theme.toolbar.text,
          },
          head: {
            fontWeight: 600,
            fontSize: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            opacity: 0.7,
            background: theme.toolbar.buttonBackgroundActive,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              background: theme.name === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            },
            '&.Mui-selected': {
              background: theme.name === 'dark' ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)',
            },
            '&.Mui-selected:hover': {
              background: theme.name === 'dark' ? 'rgba(59,130,246,0.22)' : 'rgba(37,99,235,0.13)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: theme.toolbar.buttonBorder },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: theme.toolbar.buttonBorder },
        },
      },
    },
  }), [theme]);

  return (
    <>
      <Header title="Aspect" height={56} />
      <NavigationRail
        ariaLabel="Разделы администрирования"
        theme={theme}
        items={railItems}
        onExpandedChange={setRailWidth}
      />
      <div style={{
        padding: 24,
        paddingTop: 88,
        paddingLeft: railWidth + 24,
        background: theme.background,
        minHeight: '100vh',
        color: theme.toolbar.text,
        transition: 'padding-left .16s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <h1 style={{ color: theme.toolbar.text, margin: 0, fontSize: 22, fontWeight: 700 }}>Администрирование</h1>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: theme.toolbar.buttonBackground,
              color: theme.toolbar.text,
              border: `1px solid ${theme.toolbar.buttonBorder}`,
              borderRadius: 6,
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            ← Назад
          </button>
          {projects.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <span style={{ color: theme.toolbar.text, opacity: 0.7, fontSize: 14 }}>Пространство:</span>
              <select
                value={currentProject?.id ?? ''}
                onChange={(e) => {
                  const p = projects.find((pr) => pr.id === e.target.value);
                  if (p) setCurrentProject(p as Parameters<typeof setCurrentProject>[0]);
                }}
                style={{
                  background: theme.toolbar.buttonBackground,
                  color: theme.toolbar.text,
                  border: `1px solid ${theme.toolbar.buttonBorder}`,
                  borderRadius: 6,
                  padding: '6px 10px',
                  fontSize: 14,
                  cursor: 'pointer',
                  minWidth: 180,
                }}
              >
                {!currentProject && <option value="">— выберите пространство —</option>}
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <MuiThemeProvider theme={muiTheme}>
          <Suspense fallback={<div style={{ color: theme.toolbar.text, padding: 24 }}>Загрузка…</div>}>
            <TabContent />
          </Suspense>
        </MuiThemeProvider>
      </div>
    </>
  );
};

export default AdminPage;
